import React from 'react';
import PropTypes from 'prop-types';

import { Form } from 'react-bootstrap-5';
import Select from 'react-select';

import BrokerCommissions from './broker_commissions';
import CurrencyInputWrapper from 'components/form/currency_input_wrapper';
import listingPropTypes from 'components/listing/prop_types';
import TenantRepContractDetailsForm from 'components/listing/tenant_rep/contract_details_form';
import { calculateAmount, calculatePercent, updateBrokerCommissions } from './helpers';
import ManagedInput from 'components/form/managed_input';
import ProjectLeadSearch from 'components/property/sale_buyer/project_lead_search';
import NewLeadModal from 'components/property/sale_buyer/new_lead_modal';
import { get } from 'helpers/api';
import { horizontalFormStyles, mapId } from 'helpers/react_select_helpers';
import { isEmpty, get as getLodash, camelCase } from 'lodash';

const leaseRateUnitsLabels = {
  dollars_per_sf_per_year: '$/SF/year',
  dollars_per_sf_per_month: '$/SF/month',
  dollars_per_acre_per_year: '$/Acre/year',
  dollars_per_acre_per_month: '$/Acre/month',
  dollars_per_year: 'Annual Rent',
  dollars_per_month: 'Monthly'
};

export default class ListingQuickUpdateForm extends React.Component {
  static propTypes = {
    allowStatusChange: PropTypes.bool,
    availableDatabases: PropTypes.array,
    companyPercentageLikelyToCloseDefaults: PropTypes.object,
    features: PropTypes.shape({
      pipeline: PropTypes.bool,
    }).isRequired,
    listing: listingPropTypes.isRequired,
    updatingStatusByDragging: PropTypes.bool,
    onChange: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      showNewLeadModal: false,
      referralSources: []
    };
  }

  componentDidMount = () => {
    const { closeDate, dateLikelyToClose, listingStatus } = this.props.listing;
    const changes = {};

    if (this.props.updatingStatusByDragging) {
      const percentageDefault = this.defaultPercentageLikelyToClose(listingStatus);
      if (percentageDefault) {
        changes['percentageLikelyToClose'] = percentageDefault;
      }

      if (listingStatus === 'closed' && dateLikelyToClose && !closeDate) {
        changes['closeDate'] = dateLikelyToClose;
      }
    }

    if (!isEmpty(changes)) {
      this.props.onChange(changes);
    }

    get(`/leads/referral_sources`, { params: { projectId: this.props.listing.projectId } })
      .then(referralSources => this.setState({ referralSources }));
  };

  defaultPercentageLikelyToClose = (status) => {
    return getLodash(this.props, 'companyPercentageLikelyToCloseDefaults')[camelCase(status)];
  }

  handleBrokerCommissionChange = (changes, commission) => {
    const { voucher } = this.props.listing;
    const { commissionEstimates } = voucher;
    const brokerCommissions = [...commissionEstimates.brokerCommissions];

    for (let i = 0; i < brokerCommissions.length; i++) {
      if (brokerCommissions[i].brokerId === commission.brokerId) {
        brokerCommissions[i] = { ...brokerCommissions[i], ...changes };
        break;
      }
    }

    this.props.onChange({
      voucher: {
        ...voucher,
        commissionEstimates: {
          brokerCommissions: updateBrokerCommissions(voucher.grossCommissionAmount, brokerCommissions),
        },
      }
    });
  };

  handleChange = (event) => {
    const changes = {};
    const { name, type } = event.target;
    let { value } = event.target;

    if (name === 'listingStatus') {
      const { dateLikelyToClose, closeDate } = this.props.listing;

      if (value === 'closed' && dateLikelyToClose && !closeDate) {
        changes['closeDate'] = dateLikelyToClose;
      } else if (closeDate) {
        changes['closeDate'] = null;
      }

      const percentageDefault = this.defaultPercentageLikelyToClose(value);

      if (percentageDefault) {
        changes['percentageLikelyToClose'] = percentageDefault;
      }
    }

    if (type === 'number') {
      value = value === '' ? null : parseFloat(value);
    }

    changes[name] = value;

    this.props.onChange(changes);
  };

  handleBuyerChange = (buyerLeadId, buyerLeadName) => {
    this.props.onChange({ buyerLeadId: buyerLeadId, buyerLeadName: buyerLeadName });

    if (buyerLeadId) {
      get(`/projects/${this.props.listing.projectId}/leads/${buyerLeadId}`).then((lead) => {
        if (lead.referralSource) this.props.onChange({ buyerReferralSourceId: lead.referralSource });
      });
    }
  }

  handleDateChange = (changes) => {
    if ('dateLikelyToClose' in changes) changes.daysLikelyToClose = null;

    this.props.onChange(changes);
  };

  handleDaysChange = (event) => {
    const { value } = event.target;

    this.props.onChange({
      dateLikelyToClose: null,
      daysLikelyToClose: value === '' ? null : parseInt(value)
    });
  };

  handleCurrencyChange = (event, _, value = event.target.value) => {
    const field = event.target.name;
    const floatValue = value === '' ? null : parseFloat(value);
    let changes = { [field]: floatValue };

    if (field === 'transactionValue') {
      changes = this.calculateCommissions(changes);
    } else if (field === 'grossCommissionAmount') {
      changes = { ...this.calculateCommissions({ changedField: field, changedValue: floatValue }) };
    }

    this.props.onChange(changes);
  };

  handleGrossCommissionPercentChange = (event) => {
    const grossCommissionPercent = event.target.value === '' ? null : parseFloat(event.target.value);
    const { transactionValue } = this.props.listing.voucher;

    this.props.onChange(this.calculateCommissions({
      changedField: 'grossCommissionPercent',
      changedValue: grossCommissionPercent,
      transactionValue
    }));
  };

  calculateCommissions = ({
    changedField,
    changedValue,
    transactionValue = this.props.listing.voucher.transactionValue
  }) => {
    const { voucher } = this.props.listing;
    const { commissionEstimates } = voucher;
    const changingGrossAmount = changedField === 'grossCommissionAmount';
    let calculatedField, calculatedValue;

    if (changingGrossAmount) {
      calculatedField = 'grossCommissionPercent';
    } else {
      changedField = 'grossCommissionPercent';
      calculatedField = 'grossCommissionAmount';

      if (changedValue === undefined) {
        changedValue = voucher[changedField];
      }
    }

    if (changingGrossAmount) {
      calculatedValue = calculatePercent(transactionValue, changedValue);
    } else {
      calculatedValue = calculateAmount(transactionValue, changedValue);
    }

    const grossAmount = changingGrossAmount ? changedValue : calculatedValue;

    const updatedVoucher = {
      ...voucher,
      commissionEstimates: {
        brokerCommissions: updateBrokerCommissions(grossAmount, commissionEstimates.brokerCommissions),
      },
      transactionValue,
      [calculatedField]: calculatedValue,
      [changedField]: changedValue
    };

    return { voucher: updatedVoucher };
  };

  calculateBrokerCommissions = (grossCommissionAmount) => {
    const brokerCommissions = [...this.props.listing.voucher.commissionEstimates.brokerCommissions];

    for (let i = 0; i < brokerCommissions.length; i++) {
      const commission = brokerCommissions[i];

      if (commission.grossUnit === 'gross_unit_dollars') {
        commission.grossPercent = this.calculatePercent(grossCommissionAmount, commission.grossAmount);
      } else {
        commission.grossAmount = this.calculateAmount(grossCommissionAmount, commission.grossPercent);
      }
    }

    return brokerCommissions;
  };

  render() {
    return (
      <div className="form-vertical-field-container">
        {this.renderStatus()}
        {this.renderTransactionValue()}
        {this.renderCommissions()}
        {this.renderLeaseInfo()}
        {this.renderCloseDate()}
        {this.renderBuyer()}
        {this.renderLikelyhood()}
        {this.renderDateEstimates()}
        {this.renderContractDetails()}
      </div>
    );
  }

  renderStatus() {
    if (!this.props.allowStatusChange) {
      return;
    }

    const { listingStatus, validStatuses } = this.props.listing;

    return (
      <div className="form-group">
        <label className="form-label">Status</label>
        <div className="form-input-container">
          <select
            className="form-select"
            disabled={this.props.updatingStatusByDragging}
            name="listingStatus"
            value={listingStatus}
            onChange={this.handleChange}
          >
            {validStatuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  renderTransactionValue() {
    const { transactionValue } = this.props.listing.voucher;

    return <React.Fragment>
      <div className="form-group">
        <label className="form-label">Transaction Value</label>
        <div className="form-input-container">
          <CurrencyInputWrapper
            allowEmpty
            className="form-control"
            name="transactionValue"
            value={transactionValue}
            onChangeEvent={this.handleCurrencyChange}
          />
        </div>
      </div>
    </React.Fragment>;
  }

  renderCommissions() {
    const { listing } = this.props;
    const { listingStatus, voucher } = listing;
    const { commissionEstimates } = voucher;
    const commissions = commissionEstimates.brokerCommissions;

    return <>
      <div className="fs-large">What is the {listingStatus === 'closed' ? '' : 'estimated '}gross commission?</div>

      <div className="d-flex gap-2">
        <div className="form-group">
          <label className="form-label">Gross Commission %</label>
          <input
            className="form-control"
            name="grossCommissionPercent"
            type="number"
            value={voucher.grossCommissionPercent == null ? '' : voucher.grossCommissionPercent}
            onChange={this.handleGrossCommissionPercentChange}
          />
        </div>

        <div className="form-group mt-0">
          <label className="form-label d-none d-sm-block">$</label>
          <label className="form-label d-block d-sm-none">Gross Commission $</label>
          <CurrencyInputWrapper
            allowEmpty
            className="form-control"
            name="grossCommissionAmount"
            prefix=""
            value={voucher.grossCommissionAmount}
            onChangeEvent={this.handleCurrencyChange}
          />
        </div>
      </div>

      <BrokerCommissions
        commissions={commissions.filter(commission => !commission.outside)}
        descriptor="internal broker"
        listing={listing}
        onChange={this.handleBrokerCommissionChange}
      />

      <BrokerCommissions
        commissions={commissions.filter(commission => commission.outside)}
        descriptor="outside broker"
        listing={listing}
        onChange={this.handleBrokerCommissionChange}
      />
    </>;
  }

  renderLeaseInfo() {
    const { leaseDate, leaseRate, leaseRateUnits, leaseTerm, leaseTermUnits, type } = this.props.listing;

    if (type === 'LeaseSpaceListing') {
      return (
        <div className="form-group">
          <div className="fs-large">What do you know about the lease?</div>
          <div className="form-group">
            <label className="form-label">Lease Rate</label>
            <div className="form-input-container">
              <CurrencyInputWrapper
                allowEmpty
                className="form-control"
                name="leaseRate"
                value={leaseRate}
                onChangeEvent={this.handleCurrencyChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Lease Rate Units</label>
            <div className="form-input-container">
              <select
                className="form-select"
                name="leaseRateUnits"
                value={leaseRateUnits}
                onChange={this.handleChange}
              >
                {Object.keys(leaseRateUnitsLabels).map(unit => (
                  <option key={unit} value={unit}>{leaseRateUnitsLabels[unit]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Lease Term</label>
            <div className="form-input-container">
              <input
                className="form-control"
                name="leaseTerm"
                type="number"
                value={leaseTerm == null ? '' : leaseTerm}
                onChange={this.handleChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Lease Term Units</label>
            <div className="d-flex gap-3">
              <Form.Check id="lease_term_units_months" type="radio">
                <Form.Check.Input
                  checked={leaseTermUnits === 'months'}
                  name="leaseTermUnits"
                  type="radio"
                  value="months"
                  onChange={this.handleChange}
                />
                <Form.Check.Label>Months</Form.Check.Label>
              </Form.Check>
              <Form.Check id="lease_term_units_years" type="radio">
                <Form.Check.Input
                  checked={leaseTermUnits === 'years'}
                  name="leaseTermUnits"
                  type="radio"
                  value="years"
                  onChange={this.handleChange}
                />
                <Form.Check.Label>Years</Form.Check.Label>
              </Form.Check>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Lease Begins</label>
            <div className="form-input-container datepicker-full-width">
              <ManagedInput
                className="pe-2 form-control"
                name="leaseDate"
                type="date"
                value={leaseDate}
                onChange={this.handleDateChange}
              />
            </div>
          </div>
        </div>
      );
    }
  }

  renderBuyer() {
    const { buyerLeadId, buyerLeadName, projectId, listingStatus, type } = this.props.listing;

    if (listingStatus !== 'closed' || type !== 'SaleListing') {
      return;
    }

    return (
      <div>
        <div className="form-group">
          <div className="fs-large">Who was the buyer for this listing?</div>
          <div className="form-group-stretch">
            <label className="form-label">
              Buyer
            </label>
            <div className="w-100">
              <ProjectLeadSearch
                projectId={projectId}
                selectedLead={buyerLeadId ? { id: buyerLeadId, name: buyerLeadName } : undefined}
                onLeadSelected={({ id, name }) => this.handleBuyerChange(id, name)}
                onNewLeadSelected={() => this.setState({ showNewLeadModal: true })}
              />
            </div>
          </div>
          <NewLeadModal
            leadReferralSources={[]}
            projectId={this.props.listing.projectId}
            show={this.state.showNewLeadModal}
            onHide={() => this.setState({ showNewLeadModal: false })}
            onSave={(id, name) => this.handleBuyerChange(id, name)}
          />
        </div>
        {this.state.referralSources && (
          <div className="form-group required">
            <div className="form-group-stretch">
              <label className="form-label">
                Buyer Referral Source
              </label>
              <div className="w-100">
                <Select
                  getOptionValue={mapId}
                  isClearable={true}
                  name="referralSourceSelect"
                  options={this.state.referralSources}
                  styles={horizontalFormStyles}
                  value={
                    this.state.referralSources.find(
                      referralSource => referralSource.id === this.props.listing.buyerReferralSourceId
                    )
                  }
                  onChange={referralSource => this.props.onChange({ buyerReferralSourceId: referralSource?.id })}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderCloseDate() {
    const { listing } = this.props;
    if (listing.listingStatus !== 'closed') {
      return;
    }

    return <React.Fragment>
      <div className="form-group required">
        <div className="fs-large">When did this opportunity close?</div>
        <label className="form-label">Close Date</label>
        <div className="form-input-container datepicker-full-width">
          <ManagedInput
            className="pe-2 form-control"
            name="closeDate"
            type="date"
            value={listing.closeDate}
            onChange={this.handleDateChange}
          />
        </div>
      </div>
    </React.Fragment>;
  }

  renderLikelyhood() {
    const { features: { pipeline }, listing } = this.props;
    if (!pipeline || listing.listingStatus === 'closed') {
      return;
    }

    return <React.Fragment>
      <div className="form-group">
        <div className="fs-large">What percent chance do you think the opportunity has of closing?</div>
        <label className="form-label">% likely to close</label>
        <div className="form-input-container">
          <input
            className="form-control"
            name="percentageLikelyToClose"
            type="number"
            value={listing.percentageLikelyToClose == null ? '' : listing.percentageLikelyToClose}
            onChange={this.handleChange}
          />
        </div>
      </div>
    </React.Fragment>;
  }

  renderDateEstimates() {
    const { features: { pipeline }, listing } = this.props;

    if (!pipeline || listing.listingStatus === 'closed') {
      return;
    }

    return <>
      <div className="fs-large">How many days do you think it will take to close this opportunity?</div>

      <div className="d-flex gap-2">
        <div className="form-group">
          <label className="form-label">Date</label>
          <div className="form-input-container datepicker-full-width">
            <ManagedInput
              className="text-end pe-2 form-control"
              name="dateLikelyToClose"
              type="date"
              value={listing.dateLikelyToClose}
              onChange={this.handleDateChange}
            />
          </div>
        </div>

        <div className="form-group flex-fill mt-0">
          <label className="form-label">Days</label>
          <div className="form-input-container">
            <input
              className="form-control"
              name="daysLikelyToClose"
              type="number"
              value={listing.daysLikelyToClose == null ? '' : listing.daysLikelyToClose}
              onChange={this.handleDaysChange}
            />
          </div>
        </div>
      </div>
    </>;
  }

  renderContractDetails() {
    const { listingStatus, type } = this.props.listing;
    if ((listingStatus === 'closed' || listingStatus === 'under_contract') && type === 'TenantRepListing') {
      return (
        <TenantRepContractDetailsForm
          availableDatabases={this.props.availableDatabases}
          listing={this.props.listing}
          onChange={this.props.onChange}
        />
      );
    }
  }
}
